import {toast} from 'react-toastify';
import {all, call, put, select, spawn, takeEvery, takeLatest, takeLeading} from 'redux-saga/effects';

import templateTypes from '../../../static/javascript/nano/templateTypes.json';

import {API} from '~/utils/request';

import {setPagesTypes, getParsedCourseData} from '~/utils/course';

import {
  FETCH_COURSE_DATA,
  fetchCourseDataRequest,
  fetchCourseDataSuccess,
  fetchCourseDataFailure,
  SAVE_COURSE_DATA,
  saveCourseDataRequest,
  saveCourseDataSuccess,
  saveCourseDataFailure,
  setScreenSize,
  ADD_PAGE,
  addPageCreate,
  updateThumbRequest,
  updateThumbSuccess,
  UPDATE_THUMBNAIL_IN_ACTIVE_PAGE,
  UPDATE_THUMBNAIL_IN_ALL_PAGES,
  FETCH_COURSE_DATA_SUCCESS,
  SAVE_BIT_TITLE_ANDDESCRIPTION,
  updateThumbInterupted,
  DUPLICATE_PAGE,
  addPage,
  setActivePageId,
  saveBitTitleAndDescription,
} from '../actions/course.actions';
import {
  COURSE_ON_CONTINUE_CLICK,
  COURSE_ON_GO_BACK_CLICK,
} from '~/stores/player/actions/course.actions';
import {onContinueClick, onGoBackClick} from '../../player/sagas/course.sagas';
import {SaveToast} from '~/components/SaveToast';
import {configGetConfig} from '~/stores/player/actions/config.actions';
import {getCourseData, getCourseId, getPageThumbnails, getPages} from '~/stores/player/selectors/course.selectors';
import {backendUrlV2} from '~/config';
import axios from 'axios';
import {DEFAULT_PASSING_SCORE_PERCENTAGE} from '~/modules/Editor/components/CourseSettings/static';
import {nanoid} from 'nanoid';

const THUMBNAIL_EDIT_THRESHOLD = 4000;
const DEBUG = false;

function* fetchCourseData() {
  yield put(fetchCourseDataRequest());

  try {
    const [{courseData}] = yield all([
      yield call(API.getEditorData),
      yield put(configGetConfig()),
    ]);

    setPagesTypes(templateTypes.templateTypes);

    yield put(fetchCourseDataSuccess(getParsedCourseData(courseData)));
    // sized just set to mobile size
    if (courseData.settings?.courseSize === '3') {
      yield put(setScreenSize('0'));
    }
  } catch (error) {
    console.error(error);
    toast.error('Failed to fetch course data.');
    yield put(fetchCourseDataFailure(error));
  }
}

function processsCourseDataBeforesave(data) {
  const newSettings = {...data?.settings};
  const newResultsPage = {...data?.resultsPage};

  if (!newSettings.passingCriteriaEnabled) {
    delete newSettings.passingScore;
  } else if (newSettings.passingCriteriaEnabled && !newSettings.passingScore) {
    newSettings.passingScore = DEFAULT_PASSING_SCORE_PERCENTAGE;
  }

  if (newSettings.passingScore != null) {
    newSettings.passingScore = Number.parseInt(newSettings.passingScore);
    // because result page is disabled in UI when passing score is enabled, we also need to set it here to be actually stored
    newResultsPage.disabled = false;
  }

  return {
    ...data,
    resultsPage: newResultsPage,
    settings: newSettings,
  };
}

function* saveCourseData({payload}) {
  const {course: {data} = {}} = yield select();

  if (!data) {
    toast.error('No data.');
    yield put(saveCourseDataFailure());

    return;
  }

  const {descriptionPage} = data || {};
  const {title, description} = descriptionPage || {};

  if (title != null || description != null) {
    yield put(saveBitTitleAndDescription({
      title: title?.text ?? title ?? '',
      description: description?.text ?? description ?? '',
    }));
  }

  yield put(saveCourseDataRequest());
  const dataToSubmit = processsCourseDataBeforesave(data);

  if (payload?.type === 'draft') {
    try {
      yield call(API.postCourseDraft, dataToSubmit);
      yield put(saveCourseDataSuccess());
    } catch (error) {
      console.error(error);
    }
  } else {
    try {
      yield call(API.postCourse, dataToSubmit);
      toast(SaveToast);
      yield put(saveCourseDataSuccess());
    } catch (error) {
      console.error(error);
      toast.error('Failed to save course data.');
      yield put(saveCourseDataFailure(error));
    }
  }
}

function* updateThumbnail({payload, type}) {
  const {pageId, showLoadingIndicator} = payload || {};

  if (!pageId) return;

  const courseData = yield select(getCourseData);
  const pageToUpdate = courseData?.pages?.find?.(page => page?.id === pageId);

  if (!pageToUpdate?.id) return;

  // always show loading indicator so the user sees that something is happening
  yield put(updateThumbRequest({pageId, showLoadingIndicator: true}));

  try {
    const tsStarted = pageToUpdate?.changedTs;
    const courseId = yield select(getCourseId);

    const pageUpdateData = {
      ...courseData,
      files: [],
      pages: [pageToUpdate],
      activePageId: pageId,
      backendUrl: window.location.origin,
    };

    const url = DEBUG
      ? 'http://localhost:8080/'
      : `${backendUrlV2}/editor/${courseId}/${pageId}/pageThumbnail`;

    const resp = yield axios.post(url, pageUpdateData, {withCredentials: true, responseType: 'blob'});

    // check if the page did change in the meantime
    // if it did, we dont want to set the thumbnail
    // but instead wait for the next thumbnail-operation to go on
    // setting the thumbnail would also have meant that we say that the object has the newest thumbnail
    // which then is not true
    const courseData2 = yield select(getCourseData);
    const pageToUpdate2 = courseData2.pages?.find?.(page => page?.id === pageId);
    const tsEnded = pageToUpdate2?.changedTs;

    // one url only to be used locally
    const thumbUrlLocal = URL.createObjectURL(resp.data);

    // one url to be stored in the course object and used the next time we load the bit
    const thumbUrlServer = `${url}?t=${Date.now()}`;

    if (tsEnded === tsStarted) {
      yield put(updateThumbSuccess({pageId, url: thumbUrlServer, urlLocal: thumbUrlLocal}));
    } else {
      console.log('updateThumbnail interrupted', {pageId, showLoadingIndicator, type, data: resp?.data});
      // reset so the thumbnail is ready to be generated the next time we check for thumbs to be generated
      // yield put(updateThumbInterupted({pageId, url: thumbUrlServer, urlLocal: thumbUrlLocal}));

      // update anyways since the new thumbnail will be more correct than the old one.
      yield put(updateThumbSuccess({pageId, url: thumbUrlServer, urlLocal: thumbUrlLocal}));
    }
  } catch (error) {
    console.error('Failed to genereate thumb for page id', pageId, error);
    yield put(updateThumbInterupted({pageId}));
  }
}

function* onAddPage({payload}) {
  if (!payload?.id) return;

  yield put(addPageCreate(payload));
  yield spawn(updateThumbnail, {payload: {pageId: payload.id, showLoadingIndicator: true}});
}

function* updateThumbnailForActivePage({payload}) {
  const {pageId, skipCheck} = payload || {};

  if (!pageId) return;

  let activePage;

  if (!skipCheck) {
    const pages = (yield select(getPages)) || [];

    activePage = pages.find(page => page.id === pageId);

    if (!activePage?.thumbnailRequested) return;
  }

  // if (activePage?.isProcessingThumbnail) return;
  // bugfix: since value of "page.isProcessingThumbnail" gets stored to server (why?),
  // it will stay true if any user closed the page before the thumb is loaded.
  // therefore we cannot use it to decide if we should cancel the request

  const thumbnails = yield select(getPageThumbnails);

  if (thumbnails?.[pageId]?.isFetching) return;

  const datenow = Date.now();
  const timeSinceLastEdit = datenow - (activePage?.changedTs ?? datenow);

  if (timeSinceLastEdit < THUMBNAIL_EDIT_THRESHOLD) return;

  const showLoadingIndicator = payload.showLoadingIndicator ?? true;

  yield spawn(updateThumbnail, {payload: {showLoadingIndicator, pageId}});
}

function* updateThumbnailForAllPages({payload}) {
  const pages = (yield select(getPages)) || [];
  const thumbnails = yield select(getPageThumbnails);

  for (const page of pages) {
    if (page?.id && (page.thumbnailRequested || !page?.thumbnailUrl) && !thumbnails?.[page.id]?.isFetching) {
      yield spawn(updateThumbnail, {payload: {showLoadingIndicator: false, pageId: page.id}});
    }
  }
}

function* updateThumbnailForPagesWithoutThumbnail() {
  const pages = (yield select(getPages)) || [];

  for (const page of pages) {
    if (page?.id && !page.thumbnailUrl) {
      yield spawn(updateThumbnail, {payload: {showLoadingIndicator: true, pageId: page.id, skipCheck: true}});
    }
  }
}

function* saveBitTitleAndDescriptionSaga({payload}) {
  const courseData = yield select(getCourseData);
  const {title, description} = payload || courseData?.descriptionPage || {};

  const cid = yield select(getCourseId);
  const data = {};

  if (title != null) {
    data.course = title;
  }
  if (description != null) {
    data.description = description;
  }
  if (Object.keys(data).length > 0) {
    window.parent?.postMessage?.({type: 'SAVED_TITLE_AND_DESCRIPTION', value: {title, description}}, '*');
    yield call(API.putCourseData, cid, data);
  }
}

const injectNewIds = items => {
  if (!Array.isArray(items)) return;

  items.forEach(item => {
    if (item && typeof item === 'object') item.id = nanoid();
  });
};

function* onDuplicatePage(action) {
  try {
    const {page, pageIndex} = action?.payload || {};

    const newPage = JSON.parse(JSON.stringify(page));

    [
      newPage?.pictureslides?.slides?.[0]?.items,
      newPage?.questionnaire?.answers,
      newPage?.questionnaireinput?.answers,
    ].forEach(injectNewIds);

    const id = nanoid();

    yield put(addPage({
      ...newPage,
      id,
      index: pageIndex + 1,
    }));

    yield put(setActivePageId(id));
  } catch (error) {
    console.error('duplicatePage error', error);
    toast.error('Failed to duplicate page.');
  }
}

export const sagas = [
  takeEvery(DUPLICATE_PAGE, onDuplicatePage),
  takeLatest(FETCH_COURSE_DATA, fetchCourseData),
  takeLatest(SAVE_COURSE_DATA, saveCourseData),
  takeLatest(COURSE_ON_CONTINUE_CLICK, onContinueClick),
  takeLatest(COURSE_ON_GO_BACK_CLICK, onGoBackClick),
  takeEvery(ADD_PAGE, onAddPage),
  takeEvery(UPDATE_THUMBNAIL_IN_ACTIVE_PAGE, updateThumbnailForActivePage),
  takeLatest(UPDATE_THUMBNAIL_IN_ALL_PAGES, updateThumbnailForAllPages),
  takeLeading(FETCH_COURSE_DATA_SUCCESS, updateThumbnailForPagesWithoutThumbnail),
  takeLatest(SAVE_BIT_TITLE_ANDDESCRIPTION, saveBitTitleAndDescriptionSaga),
];
